import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Target, 
  Clock, 
  AlertCircle, 
  CheckCircle, 
  Info,
  Save,
  X
} from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { apiService } from '@/services/api';

interface WhatsappConnection {
  id?: number;
  name: string;
  connection_type?: 'oficial' | 'extra_oficial';
  phone_id?: string | null;
  is_active?: boolean;
  connection_status?: string;
}

interface ApiStrategyConfig {
  connection_id: number;
  client_id: number;
  lembrete_enabled: boolean;
  cobranca_enabled: boolean;
  revistoria_enabled: boolean;
}

interface ApiStrategyModalProps {
  isOpen: boolean;
  onClose: () => void;
  connection: WhatsappConnection | null;
  clientId: number;
}

const STRATEGY_TYPES = [
  {
    key: 'lembrete',
    label: 'Lembrete',
    description: 'Responsável por envios de mensagens de lembrete antes do vencimento',
    icon: Clock,
    color: 'blue',
    badge: 'L'
  },
  {
    key: 'cobranca', 
    label: 'Cobrança',
    description: 'Responsável por envios de mensagens de cobrança após o vencimento',
    icon: AlertCircle,
    color: 'orange',
    badge: 'C'
  },
  {
    key: 'revistoria',
    label: 'Revistoria', 
    description: 'Responsável por envios de mensagens de revistoria/recuperação',
    icon: Target,
    color: 'purple',
    badge: 'R'
  }
] as const;

const ApiStrategyModal: React.FC<ApiStrategyModalProps> = ({
  isOpen,
  onClose,
  connection,
  clientId
}) => {
  console.log('🔍 [ApiStrategyModal] Modal renderizado:', { isOpen, connection, clientId });
  const [strategyConfig, setStrategyConfig] = useState<ApiStrategyConfig>({
    connection_id: 0,
    client_id: clientId,
    lembrete_enabled: false,
    cobranca_enabled: false,
    revistoria_enabled: false
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Carregar configuração existente quando o modal abrir
  useEffect(() => {
    if (isOpen && connection) {
      loadStrategyConfig();
    }
  }, [isOpen, connection]);

  const loadStrategyConfig = async () => {
    if (!connection || !connection.id) return;
    
    setLoading(true);
    try {
      console.log('🔍 [ApiStrategyModal] Carregando configuração para conexão:', connection.id);
      
      // Buscar configuração existente
      const response = await apiService.getDirect(`/api-strategies/connection/${connection.id}`);
      
      if (response.success && response.data) {
        setStrategyConfig({
          connection_id: connection.id,
          client_id: clientId,
          lembrete_enabled: response.data.lembrete_enabled || false,
          cobranca_enabled: response.data.cobranca_enabled || false,
          revistoria_enabled: response.data.revistoria_enabled || false
        });
        console.log('✅ [ApiStrategyModal] Configuração carregada:', response.data);
      } else {
        // Se não existe configuração, criar uma nova
        setStrategyConfig({
          connection_id: connection.id!,
          client_id: clientId,
          lembrete_enabled: false,
          cobranca_enabled: false,
          revistoria_enabled: false
        });
        console.log('🆕 [ApiStrategyModal] Nova configuração criada');
      }
    } catch (error: any) {
      console.error('❌ [ApiStrategyModal] Erro ao carregar configuração:', error);
      
      // Se der erro (provavelmente porque não existe), criar configuração padrão
      setStrategyConfig({
        connection_id: connection.id!,
        client_id: clientId,
        lembrete_enabled: false,
        cobranca_enabled: false,
        revistoria_enabled: false
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStrategyToggle = (strategyKey: keyof Pick<ApiStrategyConfig, 'lembrete_enabled' | 'cobranca_enabled' | 'revistoria_enabled'>) => {
    setStrategyConfig(prev => ({
      ...prev,
      [strategyKey]: !prev[strategyKey]
    }));
  };

  const handleSave = async () => {
    if (!connection || !connection.id) return;
    
    // Validar se o client_id é válido
    if (!clientId || clientId <= 0) {
      toast({
        title: "Erro de configuração",
        description: "ID do cliente inválido. Verifique se você está logado corretamente.",
        variant: "destructive"
      });
      return;
    }
    
    setSaving(true);
    try {
      console.log('💾 [ApiStrategyModal] Salvando configuração:', strategyConfig);
      
      const response = await apiService.postDirect('/api-strategies/save', strategyConfig);
      
      if (response.success) {
        toast({
          title: "Configuração salva",
          description: `Estratégias da API "${connection.name}" foram configuradas com sucesso.`,
        });
        onClose();
      } else {
        throw new Error(response.error || 'Erro ao salvar configuração');
      }
    } catch (error: any) {
      console.error('❌ [ApiStrategyModal] Erro ao salvar:', error);
      
      // Tratar erros específicos
      let errorMessage = "Erro interno do servidor";
      
      if (error.response?.status === 500) {
        const errorData = error.response?.data;
        if (errorData?.error?.includes('Key (client_id)') && errorData?.error?.includes('is not present in table "clients"')) {
          errorMessage = "Cliente não encontrado no sistema. Verifique se você está logado corretamente.";
        } else if (errorData?.error?.includes('foreign key constraint')) {
          errorMessage = "Erro de referência: Cliente ou conexão não encontrados no sistema.";
        }
      } else if (error.response?.status === 401) {
        errorMessage = "Sessão expirada. Faça login novamente.";
      } else if (error.response?.status === 404) {
        errorMessage = "Endpoint não encontrado. Verifique a configuração do servidor.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Erro ao salvar",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const getEnabledStrategiesCount = () => {
    return [
      strategyConfig.lembrete_enabled,
      strategyConfig.cobranca_enabled, 
      strategyConfig.revistoria_enabled
    ].filter(Boolean).length;
  };

  const hasAnyStrategyEnabled = getEnabledStrategiesCount() > 0;

  if (!connection) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-purple-600" />
            Configurar Estratégias - {connection.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Informações da API */}
          <Card className="border-blue-200 bg-blue-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-blue-900 flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                Informações da API
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-blue-800">Nome:</span>
                  <span className="ml-2 text-blue-700">{connection.name}</span>
                </div>
                <div>
                  <span className="font-medium text-blue-800">Tipo:</span>
                  <Badge variant="outline" className="ml-2">
                    {connection.connection_type === 'oficial' ? 'Oficial' : 'Extra Oficial'}
                  </Badge>
                </div>
                {connection.phone_id && (
                  <div>
                    <span className="font-medium text-blue-800">Phone ID:</span>
                    <span className="ml-2 text-blue-700">{connection.phone_id}</span>
                  </div>
                )}
                <div>
                  <span className="font-medium text-blue-800">Status:</span>
                  <Badge 
                    variant={connection.is_active ? "default" : "secondary"}
                    className="ml-2"
                  >
                    {connection.is_active ? 'Ativa' : 'Inativa'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Configuração de Estratégias */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5" />
                Tipos de Estratégia
              </CardTitle>
              <p className="text-sm text-gray-600">
                Selecione quais tipos de estratégia esta API será responsável por executar.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {STRATEGY_TYPES.map((strategy) => {
                const Icon = strategy.icon;
                const isEnabled = strategyConfig[`${strategy.key}_enabled` as keyof typeof strategyConfig] as boolean;
                const strategyKey = `${strategy.key}_enabled` as keyof Pick<ApiStrategyConfig, 'lembrete_enabled' | 'cobranca_enabled' | 'revistoria_enabled'>;

                return (
                  <div 
                    key={strategy.key}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      isEnabled 
                        ? `border-${strategy.color}-200 bg-${strategy.color}-50` 
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          isEnabled 
                            ? `bg-${strategy.color}-100 text-${strategy.color}-600` 
                            : 'bg-gray-100 text-gray-400'
                        }`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className={`font-medium ${
                              isEnabled ? `text-${strategy.color}-900` : 'text-gray-500'
                            }`}>
                              {strategy.label}
                            </h3>
                            <Badge 
                              variant={isEnabled ? "default" : "outline"}
                              className={`${
                                isEnabled 
                                  ? `bg-${strategy.color}-100 text-${strategy.color}-700 border-${strategy.color}-200` 
                                  : 'bg-gray-100 text-gray-500 border-gray-200'
                              }`}
                            >
                              {strategy.badge}
                            </Badge>
                          </div>
                          <p className={`text-sm ${
                            isEnabled ? `text-${strategy.color}-700` : 'text-gray-500'
                          }`}>
                            {strategy.description}
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={isEnabled}
                        onCheckedChange={() => handleStrategyToggle(strategyKey)}
                        className="data-[state=checked]:bg-blue-600"
                      />
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Resumo */}
          {hasAnyStrategyEnabled && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                <strong>Configuração ativa:</strong> Esta API será responsável por {getEnabledStrategiesCount()} tipo(s) de estratégia.
                {strategyConfig.lembrete_enabled && (
                  <div className="mt-1">• <strong>Lembrete:</strong> Mensagens antes do vencimento</div>
                )}
                {strategyConfig.cobranca_enabled && (
                  <div className="mt-1">• <strong>Cobrança:</strong> Mensagens após o vencimento</div>
                )}
                {strategyConfig.revistoria_enabled && (
                  <div className="mt-1">• <strong>Revistoria:</strong> Mensagens de recuperação</div>
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Aviso se nenhuma estratégia está ativada */}
          {!hasAnyStrategyEnabled && (
            <Alert className="border-yellow-200 bg-yellow-50">
              <Info className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800">
                <strong>Atenção:</strong> Nenhuma estratégia está ativada. Esta API não será utilizada para envios automáticos.
              </AlertDescription>
            </Alert>
          )}

          {/* Botões de ação */}
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={saving}
            >
              <X className="w-4 h-4 mr-2" />
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Salvar Configuração
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ApiStrategyModal;
